<div class="flex flex-col h-full w-full">
  <!-- Top NavBar -->
  <app-page-header>
    <div class="flex flex-row items-center gap-4 flex-1">
      @if (showBackButton) {
        <button (click)="cancel()" class="text-gray-500 hover:text-gray-700">
          <fa-icon [icon]="faChevronLeft"></fa-icon>
        </button>
      }
      <div>IDS-Check Prüfregelerstellung</div>
      @if (linkedFromOtherProject) {
        <span tooltip="Dieser myCheck wurde aus einem anderen Projekt geteilt und kann nur dort bearbeitet werden."
              tooltipDirection="bottom">
          <bic-badge bgClass="bg-gray-100" textClass="text-gray-700">Geteilt · schreibgeschützt</bic-badge>
        </span>
      }
    </div>

    <div class="flex flex-row items-center gap-4">
      @if (!readOnly) {
        <bic-button (onClick)="showImportPopup = true" buttonType="white">
          <fa-icon [icon]="faArrowUpFromBracket" class="text-sm" iconLeft></fa-icon>
          Import
        </bic-button>
      }
      <bic-button (onClick)="showExportPopup = true" buttonType="white">
        <fa-icon [icon]="faArrowDownToBracket" class="text-sm" iconLeft></fa-icon>
        Export
      </bic-button>
      @if (readOnly) {
        <bic-button (onClick)="cancel()" buttonType="white">
          Schließen
        </bic-button>
      } @else if (quickstartActive) {
        <!-- Während der Agent schreibt, ist Speichern gesperrt: das Formular hält den Stand vom
             Öffnen und würde seine Spezifikationen überschreiben. -->
        <bic-button (onClick)="cancel()" buttonType="white">
          Abbrechen
        </bic-button>
        <div tooltip="Lädt den aktuellen Stand des Agenten und gibt das Speichern frei">
          <bic-button (onClick)="leaveQuickstart(true)">
            Manuell bearbeiten
          </bic-button>
        </div>
      } @else {
        @if (canShowRuleTest) {
          <!-- Testlauf (Testmodus #2356): wie im KI-Editor links neben Abbrechen. Der Tooltip hängt
               am Wrapper, weil ein deaktivierter Button keine Maus-Events bekommt. -->
          <span class="relative inline-flex" [tooltip]="ruleTestTooltip" [tooltipSubtext]="ruleTestTooltipSubtext"
                tooltipDirection="bottom">
            <bic-button (onClick)="openRuleTest()" [disabled]="ruleTestDisabled" buttonType="white">
              <fa-icon iconLeft [icon]="faPlay"></fa-icon>
              Testlauf
            </bic-button>
            @if (ruleTestUnseen) {
              <span class="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-green" aria-label="Neues Testergebnis"></span>
            }
          </span>
        }
        <bic-button (onClick)="cancel()" buttonType="white">
          Abbrechen
        </bic-button>
        <div
          [tooltip]="!isValid ? 'Nicht alle Pflichtfelder sind ausgefüllt' : (isDirty ? 'Änderungen speichern' : 'Zurück zur Prüfregelauswahl')">
          <bic-button (onClick)="isDirty ? save() : cancel()">
            <fa-icon iconLeft [icon]="isDirty ? faFloppyDisk : faChevronLeft"></fa-icon>
            {{ isDirty ? 'Speichern' : 'Zurück' }}
          </bic-button>
        </div>
      }
    </div>
  </app-page-header>

  <!-- Vorschlags-Banner: bezieht sich auf das ganze Regelwerk, nicht nur die gewählte Spezifikation. -->
  <!-- Steht die Review-Leiste im offenen Edit-Chat, entscheidet der Nutzer dort; das Banner entfällt (#2789). -->
  @if (pendingProposal && hasOpenProposalChanges && !proposalReview.isShownInChat()) {
    <div class="px-4 pt-4">
      <app-proposal-banner
        (applyClicked)="onProposalApplyClicked()"
        (discardClicked)="onProposalDiscardClicked()"
        [proposal]="pendingProposal">
      </app-proposal-banner>
    </div>
  }

  @if (quickstartActive) {
    <!-- Schutzhinweis, gleiche Aussage wie im KI-Editor: der Chat schreibt dieses Regelwerk gerade
         serverseitig, das Formular zeigt noch den Stand vom Öffnen. -->
    <div class="mx-6 mt-6 flex flex-row items-start gap-3 rounded-lg border border-yellow-300 bg-yellow-50 p-4">
      <fa-icon [icon]="faSpinnerThird" class="mt-0.5 animate-spin text-yellow-600 text-svg-16"></fa-icon>
      <div class="flex flex-col gap-0.5">
        <span class="text-sm font-medium text-gray-900">myCheck Quickstart läuft</span>
        <span class="text-xs text-gray-600">
          chatBIC baut dieses IDS-Regelwerk gerade im Chat. Dieses Formular zeigt noch den Stand vom
          Öffnen – „Manuell bearbeiten" lädt den aktuellen Stand und gibt das Speichern frei.
        </span>
      </div>
    </div>
  }

  <!-- ===================== -->
  <!-- 01 Views: Volle Breite (kein Detail ausgewählt) -->
  <!-- ===================== -->
  @if (currentView === 'list') {
    <div class="flex flex-col flex-1 gap-4 p-4 overflow-y-auto">
      @if (submitted && validationErrors.length > 0) {
        <div class="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p class="text-sm font-medium text-yellow-800 mb-1">
            Das IDS-Regelset ist noch unvollständig. Bitte ergänze:
          </p>
          <ul class="list-disc list-inside space-y-1">
            @for (error of validationErrors; track error; let issueIndex = $index) {
              <li>
                <button (click)="openValidationIssue(issueIndex)"
                        class="text-sm text-yellow-800 text-left hover:underline">
                  {{ error }}
                </button>
              </li>
            }
          </ul>
        </div>
      }

      <!-- IDS Metadaten Banner (klickbar) -->
      <div class="group flex items-center gap-2 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
           [tooltip]="proposalNavTooltip(metadataNavStatus)" tooltipColor="dark" tooltipDirection="top"
           (click)="toggleMetadata()">
        <fa-icon [icon]="faCircleInfo" class="text-base text-gray-900"></fa-icon>
        <span class="text-sm font-medium text-gray-900 flex-1">IDS Metadaten</span>
        <app-proposal-nav-marker [status]="metadataNavStatus"></app-proposal-nav-marker>
      </div>

      <!-- Spezifikationen Header + Button -->
      <div class="flex items-center gap-2">
        <span class="text-base font-medium text-gray-900 flex-1">Spezifikationen</span>
        <bic-button (onClick)="addSpecification()" buttonType="white">
          <fa-icon [icon]="faPlus" class="text-sm" iconLeft></fa-icon>
          Neue Spezifikation
        </bic-button>
      </div>

      <!-- 01.1: Leer-State -->
      @if (rule.specifications.length === 0) {
        <app-empty-state [icon]="faFileLines" class="min-h-[240px]" message="Keine Spezifikationen">
          <bic-button (onClick)="addSpecification()" buttonType="white">
            <fa-icon [icon]="faPlus" class="text-sm" iconLeft></fa-icon>
            Spezifikation erstellen
          </bic-button>
        </app-empty-state>
      } @else {
        <!-- 01.2: Spezifikationskarten mit Zusammenfassungen -->
        @for (spec of rule.specifications; track spec.id; let i = $index) {
          <div class="group flex flex-col gap-4 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
               [tooltip]="proposalNavTooltip(specNavStatus(spec))" tooltipColor="dark" tooltipDirection="top"
               (click)="selectSpecification(i)">
            <!-- Header -->
            <div class="flex items-center gap-2">
              <fa-icon [icon]="faCube" class="text-base text-gray-900"></fa-icon>
              <span class="text-sm font-medium text-gray-900 flex-1 truncate">{{ spec.name || 'Unbenannte Spezifikation' }}</span>
              @for (version of spec.ifcVersions; track version) {
                <bic-badge type="info">{{ version }}</bic-badge>
              }
              <!-- Punkt bei Vorschlags-Änderung; das Menü erscheint bei Hover bzw. solange es offen ist. -->
              <app-proposal-nav-marker [hasMenu]="true" [menuOpen]="specDropdown.dropdown?.isVisible() ?? false"
                                       [status]="specNavStatus(spec)">
                <div (click)="$event.stopPropagation()" [bicDropdownTrigger]="specDropdown"
                     class="cursor-pointer flex-shrink-0 hover:opacity-50">
                  <fa-icon [icon]="faEllipsisV"></fa-icon>
                </div>
              </app-proposal-nav-marker>
              <bic-dropdown #specDropdown [dropdownPlacement]="'bottom-end'">
                <bic-dropdown-element (click)="$event.stopPropagation()" (onClick)="specDropdown.hide(); duplicateSpecification(i)">
                  <div class="flex flex-row text-sm gap-3 text-gray-700 items-center">
                    <fa-icon [icon]="faCopy" class="w-4"></fa-icon>
                    <span>Duplizieren</span>
                  </div>
                </bic-dropdown-element>
                <bic-dropdown-element (click)="$event.stopPropagation()" (onClick)="removeSpecification(i)">
                  <div class="flex flex-row text-sm gap-3 text-red-500 items-center">
                    <fa-icon [icon]="faTrash" class="w-4"></fa-icon>
                    <span>Löschen</span>
                  </div>
                </bic-dropdown-element>
              </bic-dropdown>
            </div>
            <!-- Zusammenfassung: Anwendbarkeit + Anforderungen -->
            <div class="flex gap-4">
              <!-- Anwendbarkeit -->
              <div class="flex flex-col flex-1 gap-2">
                <div #applicabilityContainer class="flex flex-row items-center gap-4 flex-wrap">
                  <span class="text-sm font-medium text-gray-900 flex-shrink-0">Anwendbarkeit:</span>
                  @for (facet of spec.applicability; track facet.id) {
                    <bic-badge type="info">{{ getBadgeText(facet) }}</bic-badge>
                  }
                  <span class="overflow-counter text-xs text-gray-900 flex-shrink-0"
                        style="display: none"></span>
                </div>
                <span class="text-xs text-gray-900">
                  @if (spec.applicability.length > 0) {
                    @if (spec.necessity === 'optional') { Das Modell kann Elemente mit }
                    @else if (spec.necessity === 'prohibited') { Das Modell darf keine Elemente mit }
                    @else { Das Modell muss Elemente mit }
                    @for (facet of spec.applicability; track facet.id; let last = $last) {
                      {{ getFacetDescriptionFragment(facet, 'applicability') }}
                      @if (!last) { und }
                    }
                    beinhalten.
                  }
                </span>
              </div>
              <!-- Anforderungen -->
              <div class="flex flex-col flex-1 gap-2">
                <div #requirementsContainer class="flex flex-row items-center gap-4 flex-wrap">
                  <span class="text-sm font-medium text-gray-900 flex-shrink-0">Anforderungen:</span>
                  @for (facet of spec.requirements; track facet.id) {
                    <bic-badge type="info">{{ getBadgeText(facet) }}</bic-badge>
                  }
                  <span class="overflow-counter text-xs text-gray-900 flex-shrink-0"
                        style="display: none"></span>
                </div>
                <span class="text-xs text-gray-900">
                  @if (spec.requirements.length > 0) {
                    {{ requirementsSummary(spec.requirements) }}
                  }
                </span>
              </div>
            </div>
          </div>
        }
      }
      @for (removedSpec of removedProposalSpecs; track $index) {
        <div (click)="selectRemovedSpec(removedSpec)"
             [tooltip]="proposalNavTooltip('removed')" tooltipColor="dark" tooltipDirection="top"
             class="group flex items-center gap-2 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
          <fa-icon [icon]="faCube" class="text-base text-gray-300 group-hover:text-gray-900"></fa-icon>
          <span
            class="text-sm font-medium flex-1 truncate text-gray-400 line-through group-hover:text-gray-900">{{ removedSpec.name || 'Unbenannte Spezifikation' }}</span>
          <app-proposal-nav-marker status="removed"></app-proposal-nav-marker>
        </div>
      }
    </div>
  }

  <!-- ===================== -->
  <!-- 02 Views: Split-Layout (Detail ausgewählt) -->
  <!-- ===================== -->
  @if (currentView !== 'list') {
    <div class="flex flex-row flex-1 gap-4 p-4 overflow-hidden">

      <!-- LINKE SEITE: Spezifikationsliste (schmal). Unterhalb Full HD (1920px) noch schmaler,
           Labels truncaten dann bei Bedarf statt die Leiste immer auf voller Breite zu halten
           (siehe tailwind.config.js `full-hd`). -->
      <div class="flex flex-col gap-4 w-[240px] main-full-hd:w-[423px] flex-shrink-0 overflow-y-auto">

        <!-- IDS Metadaten Banner -->
        <div class="group flex items-center gap-2 p-4 rounded-lg cursor-pointer transition-colors border"
             [class.bg-gray-100]="currentView === 'metadata'"
             [class.border-gray-300]="currentView === 'metadata'"
             [class.border-gray-200]="currentView !== 'metadata'"
             [tooltip]="proposalNavTooltip(metadataNavStatus)" tooltipColor="dark" tooltipDirection="right"
             (click)="toggleMetadata()">
          <fa-icon [icon]="faCircleInfo" class="text-base text-gray-900"></fa-icon>
          <span class="text-sm font-medium text-gray-900 flex-1 truncate">IDS Metadaten</span>
          <app-proposal-nav-marker [status]="metadataNavStatus"></app-proposal-nav-marker>
        </div>

        <!-- Spezifikationen Header + Button. In der 240px-Leiste unterhalb Full HD passt der Text
             neben der Überschrift nicht - dort nur das Plus-Icon. Icon bewusst im Inhalt statt im
             iconLeft-Slot: bic-button setzt gap-2 zwischen Slot und Text, ohne Text sässe das Icon
             sonst schief. -->
        <div class="flex items-center gap-2">
          <span class="text-base font-medium text-gray-900 flex-1 min-w-0 truncate">Spezifikationen</span>
          <bic-button (onClick)="addSpecification()" buttonType="white" tooltip="Neue Spezifikation">
            <fa-icon [icon]="faPlus" class="text-sm"></fa-icon>
            <span class="hidden main-full-hd:inline ml-2">Neue Spezifikation</span>
          </bic-button>
        </div>

        <!-- Spezifikationsliste (kompakt, ohne Zusammenfassung) -->
        @if (rule.specifications.length === 0) {
          <app-empty-state [icon]="faFileLines" class="min-h-[240px]" message="Keine Spezifikationen">
            <bic-button (onClick)="addSpecification()" buttonType="white">
              <fa-icon [icon]="faPlus" class="text-sm" iconLeft></fa-icon>
              Spezifikation erstellen
            </bic-button>
          </app-empty-state>
        } @else {
          @for (spec of rule.specifications; track spec.id; let i = $index) {
            <div class="group flex items-center gap-2 p-4 border rounded-lg cursor-pointer transition-colors"
                 [class.bg-gray-100]="selectedSpecIndex === i && currentView === 'spec-detail'"
                 [class.border-gray-300]="selectedSpecIndex === i && currentView === 'spec-detail'"
                 [class.border-gray-200]="selectedSpecIndex !== i || currentView !== 'spec-detail'"
                 [tooltip]="proposalNavTooltip(specNavStatus(spec))" tooltipColor="dark" tooltipDirection="right"
                 (click)="selectSpecification(i)">
              <fa-icon [icon]="faCube" class="text-base text-gray-900"></fa-icon>
              <span class="text-sm font-medium flex-1 truncate group-hover:text-gray-900"
                    [class.text-gray-300]="selectedSpecIndex !== i || currentView !== 'spec-detail'"
                    [class.text-gray-900]="selectedSpecIndex === i && currentView === 'spec-detail'">
                {{ spec.name || 'Unbenannte Spezifikation' }}
              </span>
              <app-proposal-nav-marker [hasMenu]="true" [menuOpen]="specDropdownSplit.dropdown?.isVisible() ?? false"
                                       [status]="specNavStatus(spec)">
                <div (click)="$event.stopPropagation()" [bicDropdownTrigger]="specDropdownSplit"
                     class="cursor-pointer flex-shrink-0 hover:opacity-50">
                  <fa-icon [icon]="faEllipsisV"></fa-icon>
                </div>
              </app-proposal-nav-marker>
              <bic-dropdown #specDropdownSplit [dropdownPlacement]="'bottom-end'">
                <bic-dropdown-element (click)="$event.stopPropagation()" (onClick)="specDropdownSplit.hide(); duplicateSpecification(i)">
                  <div class="flex flex-row text-sm gap-3 text-gray-700 items-center">
                    <fa-icon [icon]="faCopy" class="w-4"></fa-icon>
                    <span>Duplizieren</span>
                  </div>
                </bic-dropdown-element>
                <bic-dropdown-element (click)="$event.stopPropagation()" (onClick)="removeSpecification(i)">
                  <div class="flex flex-row text-sm gap-3 text-red-500 items-center">
                    <fa-icon [icon]="faTrash" class="w-4"></fa-icon>
                    <span>Löschen</span>
                  </div>
                </bic-dropdown-element>
              </bic-dropdown>
            </div>
          }
        }
        <!-- Vom Vorschlag entfernte Spezifikationen: Klick öffnet rechts die Karte mit
             „Entfernen"/„Wiederherstellen". -->
        @for (removedSpec of removedProposalSpecs; track $index) {
          <button (click)="selectRemovedSpec(removedSpec)"
                  [class]="isRemovedSpecSelected(removedSpec) ? 'bg-gray-100 border-gray-300' : 'border-gray-200 hover:bg-gray-50'"
                  [tooltip]="proposalNavTooltip('removed')" tooltipColor="dark" tooltipDirection="right"
                  class="group flex items-center gap-2 p-4 border rounded-lg text-left w-full"
                  type="button">
            <fa-icon [icon]="faCube" class="text-base text-gray-900"></fa-icon>
            <span class="text-sm font-medium flex-1 truncate line-through group-hover:text-gray-900"
                  [class.text-gray-300]="!isRemovedSpecSelected(removedSpec)"
                  [class.text-gray-900]="isRemovedSpecSelected(removedSpec)">{{ removedSpec.name || 'Unbenannte Spezifikation' }}</span>
            <app-proposal-nav-marker status="removed"></app-proposal-nav-marker>
          </button>
        }
      </div>

      <!-- RECHTE SEITE: Detail-Bereich -->
      <div class="flex flex-col flex-1 overflow-y-auto border border-gray-200 rounded-lg">
        @if (submitted && validationErrors.length > 0) {
          <div class="m-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p class="text-sm font-medium text-yellow-800 mb-1">
              Das IDS-Regelset ist noch unvollständig. Bitte ergänze:
            </p>
            <ul class="list-disc list-inside space-y-1">
              @for (error of validationErrors; track error; let issueIndex = $index) {
                <li>
                  <button (click)="openValidationIssue(issueIndex)"
                          class="text-sm text-yellow-800 text-left hover:underline">
                    {{ error }}
                  </button>
                </li>
              }
            </ul>
          </div>
        }

        @switch (currentView) {

          <!-- METADATEN VIEW -->
          @case ('metadata') {
            <div class="flex flex-col gap-4 p-5">
              <h3 class="text-base font-semibold text-gray-900">IDS Metadaten</h3>
              <div class="flex flex-row gap-4 items-end">
                <div class="flex-1">
                  <app-change-marker (acceptClicked)="onInfoFieldAccepted('title')" (rejectClicked)="onInfoFieldRejected('title')"
                                     [change]="infoFieldChange('title')" label="Titel" reviewKey="info.title">
                    <bic-input [(ngModel)]="rule.info.title" [label]="'Titel'" [placeholder]="'Titel eingeben*'" [required]="true" [showX]="false" [errorCondition]="submitted && !rule.info.title?.trim()"></bic-input>
                  </app-change-marker>
                </div>
                <div class="flex-1">
                  <app-change-marker (acceptClicked)="onInfoFieldAccepted('author')" (rejectClicked)="onInfoFieldRejected('author')"
                                     [change]="infoFieldChange('author')" label="Autor (E-Mail)" reviewKey="info.author">
                    <bic-input [(ngModel)]="rule.info.author" [label]="'Autor (E-Mail)'" [placeholder]="'E-Mail-Adresse eingeben*'" [required]="true" [showX]="false" [errorCondition]="isAuthorInvalid"></bic-input>
                  </app-change-marker>
                </div>
              </div>
              <div class="flex flex-row gap-4 items-end">
                <div class="flex-1">
                  <app-change-marker (acceptClicked)="onInfoFieldAccepted('description')" (rejectClicked)="onInfoFieldRejected('description')"
                                     [change]="infoFieldChange('description')" label="Beschreibung" reviewKey="info.description">
                    <bic-input [(ngModel)]="rule.info.description" [label]="'Beschreibung'" [placeholder]="'Beschreibung eingeben*'" [showX]="false"></bic-input>
                  </app-change-marker>
                </div>
                <div class="flex-1">
                  <app-change-marker (acceptClicked)="onInfoFieldAccepted('version')" (rejectClicked)="onInfoFieldRejected('version')"
                                     [change]="infoFieldChange('version')" label="Version" reviewKey="info.version">
                    <bic-input [(ngModel)]="rule.info.version" [label]="'Version'" [placeholder]="'Version eingeben*'" [showX]="false"></bic-input>
                  </app-change-marker>
                </div>
              </div>
              <div class="flex flex-row gap-4 items-end">
                <div class="flex-1">
                  <app-change-marker (acceptClicked)="onInfoFieldAccepted('date')" (rejectClicked)="onInfoFieldRejected('date')"
                                     [change]="infoFieldChange('date')" label="Datum" reviewKey="info.date">
                    <bic-input [(ngModel)]="rule.info.date" bicDatepickerInput [isDatepicker]="true" [iconStart]="faCalendarDays" [minDateToday]="false" [label]="'Datum'" [placeholder]="'Datum auswählen*'" [showX]="false" [errorCondition]="isDateInvalid" (dateChange)="rule.info.date = formatIdsDate($event)"></bic-input>
                  </app-change-marker>
                </div>
                <div class="flex-1">
                  <app-change-marker (acceptClicked)="onInfoFieldAccepted('purpose')" (rejectClicked)="onInfoFieldRejected('purpose')"
                                     [change]="infoFieldChange('purpose')" label="Zweck" reviewKey="info.purpose">
                    <bic-input [(ngModel)]="rule.info.purpose" [label]="'Zweck'" [placeholder]="'Zweck eingeben*'" [showX]="false"></bic-input>
                  </app-change-marker>
                </div>
              </div>
              <div class="flex flex-row gap-4 items-end">
                <div class="flex-1">
                  <app-change-marker (acceptClicked)="onInfoFieldAccepted('milestone')" (rejectClicked)="onInfoFieldRejected('milestone')"
                                     [change]="infoFieldChange('milestone')" label="Meilenstein" reviewKey="info.milestone">
                    <bic-input [(ngModel)]="rule.info.milestone" [label]="'Meilenstein'" [placeholder]="'Meilenstein eingeben*'" [showX]="false"></bic-input>
                  </app-change-marker>
                </div>
                <div class="flex-1">
                  <app-change-marker (acceptClicked)="onInfoFieldAccepted('copyright')" (rejectClicked)="onInfoFieldRejected('copyright')"
                                     [change]="infoFieldChange('copyright')" label="Urheberrechte" reviewKey="info.copyright">
                    <bic-input [(ngModel)]="rule.info.copyright" [label]="'Urheberrechte'" [placeholder]="'Urheberrechte eingeben*'" [showX]="false"></bic-input>
                  </app-change-marker>
                </div>
              </div>

              <!-- Farbcodierung des Berichts (#2309). Ohne Haken faerbt der Bericht wie bisher
                   binaer ueber den IDS-Status - deshalb ist das eine bewusste Entscheidung und
                   keine stille Vorbelegung. -->
              <div class="flex flex-col gap-4 pt-4 border-t border-gray-200">
                <div class="flex flex-col gap-1">
                  <h3 class="text-base font-semibold text-gray-900">Farbcodierung des Berichts</h3>
                  <span class="text-sm text-gray-500">Legt fest, ab welchem Ergebnis eine Prüfung im
                    Bericht als erfüllt oder teilweise erfüllt gilt. Gilt auch für den PDF- und
                    Excel-Export.</span>
                </div>

                <label class="flex items-center gap-2 cursor-pointer w-fit">
                  <input type="checkbox"
                         [checked]="thresholdsEnabled"
                         (change)="toggleThresholds()"
                         [disabled]="readOnly"
                         class="w-4 h-4 rounded border-gray-300 text-teal-500 focus:ring-teal-500">
                  <span class="text-sm font-medium text-gray-900">Eigene Fehlerstufen verwenden</span>
                </label>

                @if (thresholdsEnabled) {
                  <div class="flex flex-row gap-4 items-start">
                    <div class="flex-1">
                      <bic-input [(ngModel)]="thresholdWarningFrom"
                                 (ngModelChange)="onThresholdChange()"
                                 [disabled]="readOnly"
                                 [label]="'Warnung ab (%)'"
                                 [max]="100" [min]="0"
                                 [placeholder]="'z.B. 50'"
                                 [showX]="false"
                                 [errorCondition]="thresholdError !== null"
                                 [type]="'number'"></bic-input>
                    </div>
                    <div class="flex-1">
                      <bic-input [(ngModel)]="thresholdSuccessFrom"
                                 (ngModelChange)="onThresholdChange()"
                                 [disabled]="readOnly"
                                 [label]="'Erfüllt ab (%)'"
                                 [max]="100" [min]="0"
                                 [placeholder]="'z.B. 100'"
                                 [showX]="false"
                                 [errorCondition]="thresholdError !== null"
                                 [type]="'number'"></bic-input>
                    </div>
                  </div>

                  @if (thresholdError) {
                    <span class="text-sm text-red">{{ thresholdError }}</span>
                  } @else {
                    <!-- Vorschau: nennt die drei Bereiche genau so, wie der Bericht sie faerbt. -->
                    <div class="flex flex-row flex-wrap gap-2 text-xs">
                      @for (band of thresholdPreview; track band.label) {
                        <span [class]="band.classes"
                              class="px-2 py-1 rounded font-medium">{{ band.label }}</span>
                      }
                    </div>
                  }
                }
              </div>
            </div>
        }

          <!-- VOM VORSCHLAG ENTFERNTE SPEZIFIKATION -->
          @case ('removed-spec') {
            @if (selectedRemovedSpec; as removedSpec) {
              <div class="p-4">
                <app-removed-rule-card (removeClicked)="acceptSpecRemoval(removedSpec)"
                                       (restoreClicked)="rejectSpecRemoval(removedSpec)"
                                       [itemName]="removedSpec.name || 'Unbenannte Spezifikation'"
                                       itemLabel="Spezifikation">
                </app-removed-rule-card>
              </div>
            }
          }

          <!-- SPEC-DETAIL VIEW -->
          @case ('spec-detail') {
            @if (selectedSpec) {
              <div class="flex flex-col h-full">
                <!-- Spezifikation Name -->
                <div class="flex items-center justify-between px-5 pt-5 pb-2 flex-shrink-0">
                  <h3 class="text-base font-semibold text-gray-900">{{ selectedSpec.name || 'Unbenannte Spezifikation' }}</h3>
                  @if (isProposalSpecAdded(selectedSpec)) {
                    <!-- Eine neue Spezifikation hat keine einzelnen geänderten Felder: ✓/✗ gelten für sie als Ganzes. -->
                    <div class="flex items-center gap-2">
                      <app-proposal-status-badge status="added"></app-proposal-status-badge>
                      <app-proposal-actions (acceptClicked)="acceptSpecProposal(selectedSpec)"
                                            (rejectClicked)="rejectSpecProposal(selectedSpec)"></app-proposal-actions>
                    </div>
                  }
                </div>

                <!-- Button Group Tabs -->
                <div class="flex flex-row items-center px-5 pb-4 flex-shrink-0">
                  <div class="flex flex-row border border-gray-200 rounded-lg overflow-hidden">
                    <button class="px-3 py-2 text-sm font-medium border-r border-gray-200 transition-colors"
                            [class.bg-gray-100]="activeDetailTab === 'general'"
                            [class.text-gray-900]="activeDetailTab === 'general'"
                            [class.bg-white]="activeDetailTab !== 'general'"
                            [class.text-gray-400]="activeDetailTab !== 'general'"
                            (click)="activeDetailTab = 'general'">
                      Allgemein
                      @if (specTabHasProposalChange('general')) {
                        <span class="proposal-tab-dot inline-block ml-1 align-middle w-1.5 h-1.5 rounded-full bg-green-500"></span>
                      }
                    </button>
                    <button class="px-3 py-2 text-sm font-medium border-r border-gray-200 transition-colors"
                            [class.bg-gray-100]="activeDetailTab === 'applicability'"
                            [class.text-gray-900]="activeDetailTab === 'applicability'"
                            [class.bg-white]="activeDetailTab !== 'applicability'"
                            [class.text-gray-400]="activeDetailTab !== 'applicability'"
                            (click)="activeDetailTab = 'applicability'">
                      Anwendbarkeit
                      @if (specTabHasProposalChange('applicability')) {
                        <span class="proposal-tab-dot inline-block ml-1 align-middle w-1.5 h-1.5 rounded-full bg-green-500"></span>
                      }
                    </button>
                    <button class="px-3 py-2 text-sm font-medium transition-colors"
                            [class.bg-gray-100]="activeDetailTab === 'requirements'"
                            [class.text-gray-900]="activeDetailTab === 'requirements'"
                            [class.bg-white]="activeDetailTab !== 'requirements'"
                            [class.text-gray-400]="activeDetailTab !== 'requirements'"
                            (click)="activeDetailTab = 'requirements'">
                      Anforderungen
                      @if (specTabHasProposalChange('requirements')) {
                        <span class="proposal-tab-dot inline-block ml-1 align-middle w-1.5 h-1.5 rounded-full bg-green-500"></span>
                      }
                    </button>
                  </div>
                </div>

                <!-- Tab Content -->
                <div class="flex-1 overflow-y-auto px-5 pb-5">
                  <app-ids-specification
                    [specification]="selectedSpec"
                    [activeTab]="activeDetailTab"
                    [submitted]="submitted"
                    [fieldChanges]="specFieldChanges(selectedSpec)"
                    [reviewKeyPrefix]="specReviewKeyPrefix(selectedSpec)"
                    (fieldAccepted)="onSpecFieldAccepted(selectedSpec, $event)"
                    (fieldRejected)="onSpecFieldRejected(selectedSpec, $event)"
                    (facetAccepted)="onSpecFacetAccepted(selectedSpec, $event.block, $event.index)"
                    (facetRejected)="onSpecFacetRejected(selectedSpec, $event.block, $event.index)"
                    [removedFacets]="specRemovedFacets(selectedSpec)"
                    (removedFacetAccepted)="acceptFacetRemoval(selectedSpec, $event)"
                    (removedFacetRejected)="rejectFacetRemoval(selectedSpec, $event)"
                    (specificationChange)="onSpecificationChange($event)">
                  </app-ids-specification>
                </div>
              </div>
            }
          }
        }
      </div>
    </div>
  }
</div>

<!-- Hidden file inputs -->
<input #idsFileInput type="file" accept=".ids,.xml" (change)="onIdsFileSelected($event)" class="hidden">
<input #idsExcelFileInput type="file" accept=".xlsx,.xls" (change)="onIdsExcelFileSelected($event)" class="hidden">
<input #attributeListFileInput type="file" accept=".xlsx,.xls" (change)="onAttributeListFileSelected($event)" class="hidden">
<input #revitFileInput type="file" accept=".txt" (change)="onRevitFileSelected($event)" class="hidden">

<!-- Import Popup -->
@if (showImportPopup) {
  <bic-popup [title]="'IDS-Check Vorlagen-Import'" (close)="toggleImportPopup()" [hasBackdrop]="true" class="absolute">
    <div bicPopupContent class="flex flex-col gap-4 text-gray-900 text-sm items-start justify-start w-[780px]">
      <div class="flex flex-col gap-2">
        <span>Das Prüfregelset des IDS-Checks kann durch den Import einer <span class="font-medium text-black">IDS-Vorlage</span>, einer <span class="font-medium text-black">Attributliste</span> oder einer <span class="font-medium text-black">IDS-Datei</span> erstellt werden.</span>
        <span>Hierzu werden Vorlagen bereitgestellt, welche für eine ordnungsgemäße Verwendung <span class="font-medium text-black">zwingend</span> zu nutzen sind.</span>
      </div>

      <div class="flex flex-col gap-2 w-full min-w-0">
        <span class="font-medium text-black">Vorlagen zum Herunterladen</span>
        <div class="flex flex-col gap-3 w-full rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div class="flex flex-row items-center justify-between gap-4">
            <div class="flex flex-col min-w-0">
              <span class="font-medium">IDS-Vorlage (.xlsx)</span>
              <span class="text-xs text-gray-500">Excel-Vorlage zum Anlegen von Bauteilen und Eigenschaften im IDS-Format.</span>
            </div>
            <bic-button class="shrink-0" (onClick)="downloadIdsTemplate()" buttonType="white">
              Herunterladen
              <fa-icon iconLeft [icon]="faArrowDownToBracket"></fa-icon>
            </bic-button>
          </div>
          <div class="border-t border-gray-200"></div>
          <div class="flex flex-row items-center justify-between gap-4">
            <div class="flex flex-col min-w-0">
              <span class="font-medium">Attributliste (.xlsx)</span>
              <span class="text-xs text-gray-500">Excel-Vorlage zur Erfassung von Attributen je Bauteil, z.B. anhand des LOI.</span>
            </div>
            <bic-button class="shrink-0" (onClick)="downloadAttributeListTemplate()" buttonType="white">
              Herunterladen
              <fa-icon iconLeft [icon]="faArrowDownToBracket"></fa-icon>
            </bic-button>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2 w-full min-w-0">
        <span class="font-medium text-black">Datei zum Import auswählen</span>

        @if (!selectedImportFileName) {
          <div class="flex flex-col gap-3 w-full rounded-lg border border-teal-100 bg-teal-500/5 p-3">
            <div class="flex flex-row items-center justify-between gap-4">
              <div class="flex flex-col min-w-0">
                <span class="font-medium">IDS-Datei (.ids/.xml)</span>
                <span class="text-xs text-gray-500">Bestehende IDS-Datei direkt als Prüfregelset importieren.</span>
              </div>
              <bic-button class="shrink-0" (onClick)="idsFileInput.click()" buttonType="white">
                Auswählen
                <fa-icon iconLeft [icon]="faArrowUpFromBracket"></fa-icon>
              </bic-button>
            </div>
            <div class="border-t border-teal-100"></div>
            <div class="flex flex-row items-center justify-between gap-4">
              <div class="flex flex-col min-w-0">
                <span class="font-medium">IDS-Vorlage (.xlsx)</span>
                <span class="text-xs text-gray-500">Ausgefüllte IDS-Vorlage importieren.</span>
              </div>
              <bic-button class="shrink-0" (onClick)="idsExcelFileInput.click()" buttonType="white">
                Auswählen
                <fa-icon iconLeft [icon]="faArrowUpFromBracket"></fa-icon>
              </bic-button>
            </div>
            <div class="border-t border-teal-100"></div>
            <div class="flex flex-row items-center justify-between gap-4">
              <div class="flex flex-col min-w-0">
                <span class="font-medium">Attributliste (.xlsx)</span>
                <span class="text-xs text-gray-500">Ausgefüllte Attributliste importieren.</span>
              </div>
              <bic-button class="shrink-0" (onClick)="attributeListFileInput.click()" buttonType="white">
                Auswählen
                <fa-icon iconLeft [icon]="faArrowUpFromBracket"></fa-icon>
              </bic-button>
            </div>
          </div>
        }

        @if (selectedImportFileName) {
          <div class="flex flex-row gap-4 w-full min-w-0">
            <bic-button class="w-full min-w-0 overflow-hidden" (onClick)="reopenFileInput()" buttonType="white"
                        [disabled]="importCompleted">
              <span class="block w-full min-w-0 truncate text-left">{{ selectedImportFileName }}</span>
              <fa-icon iconLeft [icon]="faFile"></fa-icon>
            </bic-button>
          </div>
        }
      </div>

      <!-- Attributlisten-Import: Zusatzfelder -->
      @if (selectedImportFileName && importType === 'attribute-list') {
        <div class="flex flex-row gap-4 w-full">
          <bic-select class="w-full" [(value)]="ifcVersion" [label]="'IFC-Version *'"
                      [placeholder]="'IFC-Version auswählen'" [disabled]="importCompleted">
            @for (option of attributeListIfcVersions; track option) {
              <bic-dropdown-element [value]="option">{{ option }}</bic-dropdown-element>
            }
          </bic-select>

          <bic-select class="w-full" [(value)]="levelOfInformation" [label]="'Level Of Information *'"
                      [placeholder]="'LOI auswählen'" [disabled]="importCompleted">
            @for (loi of [100, 200, 300, 400, 500]; track loi) {
              <bic-dropdown-element [value]="loi">{{ loi }}</bic-dropdown-element>
            }
          </bic-select>

          <bic-input class="w-full" [label]="'Verantwortliche Disziplin'"
                     [(value)]="discipline" [placeholder]="'Disziplin eingeben'" [showX]="false"
                     [disabled]="importCompleted">
          </bic-input>
        </div>
      }

      <!-- Import-Optionen: Verhalten bei bereits vorhandenen Bauteilen/Metadaten -->
      @if (selectedImportFileName) {
        <div class="flex flex-col gap-2 w-full">
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="checkbox"
                   [checked]="importReplaceSpecifications"
                   (change)="importReplaceSpecifications = !importReplaceSpecifications"
                   [disabled]="importCompleted"
                   class="w-4 h-4 rounded border-gray-300 text-teal-500 focus:ring-teal-500">
            <span class="text-sm font-medium text-gray-900">Bestehende Bauteile durch den Import ersetzen (statt zusätzlich hinzuzufügen)</span>
          </label>
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="checkbox"
                   [checked]="importOverwriteMetadata"
                   (change)="importOverwriteMetadata = !importOverwriteMetadata"
                   [disabled]="importCompleted"
                   class="w-4 h-4 rounded border-gray-300 text-teal-500 focus:ring-teal-500">
            <span class="text-sm font-medium text-gray-900">Metadaten durch den Import überschreiben (inkl. myCheck-Name)</span>
          </label>
        </div>
      }

      <!-- Status / Fehlermeldung des Imports -->
      @if (importResponseMessage) {
        <div class="flex flex-col gap-2 w-full min-w-0">
          <div class="flex flex-row items-center gap-3 flex-wrap min-w-0">
            <span class="text-sm break-words" [class.text-red-600]="!importCompleted">{{ importResponseMessage }}</span>
            @if (importErrorDetails) {
              <button type="button" class="text-sm underline text-gray-600 hover:text-gray-900 shrink-0"
                      (click)="toggleImportErrorDetails()">
                {{ showImportErrorDetails ? 'Details verbergen' : 'Details anzeigen' }}
              </button>
            }
          </div>
          @if (showImportErrorDetails && importErrorDetails) {
            <pre class="w-full max-h-40 overflow-auto rounded border border-gray-200 bg-gray-50 p-2 text-[10px] leading-tight whitespace-pre-wrap break-words select-text">{{ importErrorDetails }}</pre>
          }
        </div>
      }
    </div>

    <div bicPopupButtonGroup class="flex flex-row gap-4 items-center justify-end w-full">
      <div class="flex flex-row gap-4">
        <bic-button (onClick)="toggleImportPopup()" buttonType="white" [disabled]="importCompleted">Abbrechen</bic-button>

        @if (importCompleted) {
          <bic-button (onClick)="toggleImportPopup()">Weiter</bic-button>
        } @else {
          <bic-button (onClick)="startImport()"
                      [loading]="importLoading"
                      [disabled]="importLoading || !selectedImportFileName || (importType === 'attribute-list' && (!ifcVersion || !levelOfInformation))">
            Importieren
          </bic-button>
        }
      </div>
    </div>
  </bic-popup>
}

@if (showExportPopup) {
  <bic-popup [title]="'Export'" (close)="toggleExportPopup()" [hasBackdrop]="true" class="absolute">
    <div bicPopupContent class="flex flex-col gap-4 text-gray-900 text-sm items-start justify-start w-[640px]">
      <div class="flex flex-row border border-gray-200 rounded-lg overflow-hidden self-start">
        <button class="px-3 py-2 text-sm font-medium border-r border-gray-200 transition-colors"
                [class.bg-gray-100]="exportTab === 'ids'"
                [class.text-gray-900]="exportTab === 'ids'"
                [class.bg-white]="exportTab !== 'ids'"
                [class.text-gray-400]="exportTab !== 'ids'"
                (click)="exportTab = 'ids'">
          IDS
        </button>
        <button class="px-3 py-2 text-sm font-medium border-r border-gray-200 transition-colors"
                [class.bg-gray-100]="exportTab === 'ids-vorlage'"
                [class.text-gray-900]="exportTab === 'ids-vorlage'"
                [class.bg-white]="exportTab !== 'ids-vorlage'"
                [class.text-gray-400]="exportTab !== 'ids-vorlage'"
                (click)="exportTab = 'ids-vorlage'">
          IDS-Vorlage
        </button>
        <button class="px-3 py-2 text-sm font-medium border-r border-gray-200 transition-colors"
                [class.bg-gray-100]="exportTab === 'attributliste'"
                [class.text-gray-900]="exportTab === 'attributliste'"
                [class.bg-white]="exportTab !== 'attributliste'"
                [class.text-gray-400]="exportTab !== 'attributliste'"
                (click)="exportTab = 'attributliste'">
          Attributliste
        </button>
        <button class="px-3 py-2 text-sm font-medium transition-colors"
                [class.bg-gray-100]="exportTab === 'revit'"
                [class.text-gray-900]="exportTab === 'revit'"
                [class.bg-white]="exportTab !== 'revit'"
                [class.text-gray-400]="exportTab !== 'revit'"
                (click)="exportTab = 'revit'">
          Shared Parameters
        </button>
      </div>

      @if (exportTab === 'ids' || exportTab === 'ids-vorlage' || exportTab === 'attributliste') {
        <div class="flex flex-col gap-2 w-full min-h-24">
          @if (exportTab === 'ids') {
            <span>Exportiert das gesamte Prüfregelset dieses Checks als Standard-<span class="font-medium text-black">IDS-Datei (.ids)</span>.</span>
          }

          @if (exportTab === 'ids-vorlage') {
            <span>Exportiert das gesamte Prüfregelset dieses Checks als befüllte <span class="font-medium text-black">IDS-Vorlage (.xlsx)</span>. Die Excel-Datei lässt sich anschließend wieder über den IDS-Vorlage-Import einlesen.</span>
            @if (excelExportErrorMessage) {
              <span class="text-sm text-red-600">{{ excelExportErrorMessage }}</span>
            }
          }

          @if (exportTab === 'attributliste') {
            <span>Exportiert die Eigenschafts-Anforderungen dieses Checks als <span class="font-medium text-black">Attributliste (.xlsx)</span>, ein Tabellenblatt je Spezifikation. Da Anforderungen kein Detaillierungsgrad-Level (LOI) kennen, werden alle LOI-Stufen (100–500) als erforderlich markiert.</span>
            @if (excelExportErrorMessage) {
              <span class="text-sm text-red-600">{{ excelExportErrorMessage }}</span>
            }
          }
        </div>
      }

      @if (exportTab === 'revit') {
      <span>Erzeugt aus den Anforderungen dieses IDS-Checks eine <span class="font-medium text-black">Revit-Shared-Parameter-Datei (.txt)</span>. Eine Zeile pro geforderter Eigenschaft, gruppiert nach der gewählten Option unten.</span>

      @if (!selectedRevitFileName) {
        <div class="flex flex-col gap-2 w-full">
          <span>Bereits vorhandene Shared-Parameter-Datei (optional):</span>
          <bic-button class="shrink-0 self-start" (onClick)="revitFileInput.click()" buttonType="white">
            Datei hochladen (.txt)
            <fa-icon iconLeft [icon]="faArrowUpFromBracket"></fa-icon>
          </bic-button>
          <span class="text-xs text-gray-500">Sorgt dafür, dass unveränderte Parameter ihre bisherige GUID behalten, statt in Revit neu gemappt werden zu müssen.</span>
        </div>
      } @else {
        <div class="flex flex-col gap-2 w-full">
          <span>Bereits vorhandene Shared-Parameter-Datei (optional):</span>
          <bic-button class="w-full min-w-0 overflow-hidden self-start" (onClick)="reopenRevitFileInput()" buttonType="white">
            <span class="block w-full min-w-0 truncate text-left">{{ selectedRevitFileName }}</span>
            <fa-icon iconLeft [icon]="faFile"></fa-icon>
          </bic-button>
        </div>
      }

      <div class="flex flex-row gap-4 w-full">
        <bic-select class="w-full" [(value)]="revitExportGroupBy" [label]="'Gruppierung'">
          <bic-dropdown-element [value]="'specification'">Nach Spezifikation</bic-dropdown-element>
          <bic-dropdown-element [value]="'propertySet'">Nach PropertySet</bic-dropdown-element>
          <bic-dropdown-element [value]="'none'">Keine Gruppierung</bic-dropdown-element>
        </bic-select>

        <bic-select class="w-full" [(value)]="revitExportNameScheme" [label]="'Parametername'">
          <bic-dropdown-element [value]="'property'">Nur Eigenschaftsname</bic-dropdown-element>
          <bic-dropdown-element [value]="'propertySetProperty'">PropertySet.Eigenschaftsname</bic-dropdown-element>
        </bic-select>
      </div>

      <div class="flex flex-col gap-1 w-full">
        <bic-select class="w-full" [(value)]="revitExportFallbackDataType" [label]="'Fallback-Datentyp'">
          @for (dataType of revitFallbackDataTypes; track dataType) {
            <bic-dropdown-element [value]="dataType">{{ dataType }}</bic-dropdown-element>
          }
        </bic-select>
        <span class="text-xs text-gray-500">Wird für Eigenschaften ohne festgelegten oder nicht zuordenbaren Datentyp verwendet.</span>
      </div>

      <div class="flex flex-col gap-1 w-full">
        <div class="flex flex-row gap-4 w-full">
          <bic-input class="w-full" [label]="'Eigenschaften ausschließen'" [(value)]="revitExportExcludedProperties"
                     [placeholder]="'z.B. BIC_Geschoss; BIC_*'" [showX]="false"></bic-input>

          <bic-input class="w-full" [label]="'PropertySets ausschließen'" [(value)]="revitExportExcludedPropertySets"
                     [placeholder]="'z.B. Pset_WallCommon; Pset_Intern*'" [showX]="false"></bic-input>
        </div>
        <span class="text-xs text-gray-500">Mehrere Einträge mit Komma oder Semikolon trennen. Groß-/Kleinschreibung spielt keine Rolle, ein <span class="font-medium">*</span> am Ende wirkt als Platzhalter.</span>
      </div>

      <label class="flex items-center gap-2 cursor-pointer">
        <input type="checkbox"
               [checked]="revitExportKeepOrphans"
               (change)="revitExportKeepOrphans = !revitExportKeepOrphans"
               class="w-4 h-4 rounded border-gray-300 text-teal-500 focus:ring-teal-500">
        <span class="text-sm font-medium text-gray-900">Nicht mehr vorhandene Parameter aus der Altdatei behalten</span>
      </label>

      @if (revitExportResultMessage) {
        <div class="flex flex-col gap-2 w-full min-w-0">
          <span class="text-sm break-words">{{ revitExportResultMessage }}</span>
          @if (revitExportWarnings) {
            <ul class="list-disc pl-5 text-xs text-gray-600">
              @for (warning of revitExportWarnings; track warning) {
                <li>{{ warning }}</li>
              }
            </ul>
          }
        </div>
      }
      }
    </div>

    <div bicPopupButtonGroup class="flex flex-row gap-4 items-center justify-end w-full">
      <bic-button (onClick)="toggleExportPopup()" buttonType="white">Schließen</bic-button>
      @if (exportTab === 'ids') {
        <bic-button (onClick)="exportIDS()">
          Exportieren
          <fa-icon iconLeft [icon]="faArrowDownToBracket"></fa-icon>
        </bic-button>
      } @else if (exportTab === 'ids-vorlage') {
        <bic-button (onClick)="exportIdsVorlage()" [disabled]="excelExportLoading">
          Exportieren
          <fa-icon iconLeft [icon]="excelExportLoading ? faSpinnerThird : faArrowDownToBracket" [class.fa-spin]="excelExportLoading"></fa-icon>
        </bic-button>
      } @else if (exportTab === 'attributliste') {
        <bic-button (onClick)="exportAttributliste()" [disabled]="excelExportLoading">
          Exportieren
          <fa-icon iconLeft [icon]="excelExportLoading ? faSpinnerThird : faArrowDownToBracket" [class.fa-spin]="excelExportLoading"></fa-icon>
        </bic-button>
      } @else {
        <bic-button (onClick)="exportRevitSharedParameters()">
          Exportieren
          <fa-icon iconLeft [icon]="faArrowDownToBracket"></fa-icon>
        </bic-button>
      }
    </div>
  </bic-popup>
}

@if (specIndexToRemove !== null) {
  <bic-popup (close)="cancelRemoveSpecification()" [hasBackdrop]="true" customClass="w-[560px]"
             title="Spezifikation entfernen?">
    <div bicPopupContent class="flex flex-col gap-4">
      <span class="font-normal">Die Spezifikation „{{ specNameToRemove }}" wird entfernt, sobald Sie die Prüfregel
        speichern.</span>
      <app-rule-usage-warning [loading]="deleteUsagesLoading" [usages]="deleteUsages"></app-rule-usage-warning>
    </div>
    <div bicPopupButtonGroup class="flex flex-row w-full justify-end items-center gap-4">
      <bic-button (onClick)="cancelRemoveSpecification()" buttonType="white">Abbrechen</bic-button>
      <bic-button (onClick)="confirmRemoveSpecification()" [disabled]="deleteUsagesLoading" buttonType="primary">
        Entfernen
      </bic-button>
    </div>
  </bic-popup>
}

@if (ruleTestContext) {
  <app-rule-test-popup [context]="ruleTestContext" (closed)="closeRuleTest()"></app-rule-test-popup>
}
